import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';
import { getSingleStudent, updateStudent } from '../../helper/student_helper';
import { toast } from 'react-toastify';

const Profile = ({ setLoader, profileData, setProfileData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const fetchStudentDetails = async (studentId) => {
    try {
      setLoader(true);
      const data = await getSingleStudent(studentId);
      if (data?.success) {
        setProfileData(data?.result?.[0]);
      } else {
        toast.error(data?.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    const studentId = JSON.parse(localStorage.getItem('student'))?.id;
    if (studentId && !profileData) {
      fetchStudentDetails(studentId);
    }
  }, [profileData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.includes('.')) {
      const [parentField, childField] = name.split('.');
      setProfileData((prevState) => ({
        ...prevState,
        [parentField]: {
          ...prevState[parentField],
          [childField]: value
        }
      }));
    } else {
      setProfileData({
        ...profileData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Profile Data: ", profileData);

    try {
      setLoader(true);
      const data = await updateStudent(profileData);
      if (data?.success) {
        toast.success(data?.message);
        setIsEditing(false);
      } else {
        toast.error(data?.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  const handleCancel = (e) => {
    e.preventDefault();
    setIsEditing(false);
    // Optionally reset to original data if needed
  };

  const handleEditToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditing(!isEditing);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          <Box
            component="form"
            id="profileForm"
            onSubmit={handleSubmit}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                mb: 3
              }}
            >
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Profile
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {isEditing ? (
                  <>
                    <Tooltip title="Save Changes">
                      <IconButton
                        type="submit"
                        color="primary"
                        sx={{
                          '&:hover': { bgcolor: 'primary.light' },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <SaveIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton 
                        type="button"
                        onClick={handleCancel} 
                        color="secondary"
                        sx={{
                          '&:hover': { bgcolor: 'error.light' },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <Tooltip title="Edit Profile">
                    <IconButton
                      type="button"
                      onClick={handleEditToggle}
                      color="primary"
                      sx={{
                        '&:hover': { 
                          bgcolor: 'primary.light', 
                          transform: 'scale(1.05)' 
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
              Add information about yourself
            </Typography>

            {/* Personal Information Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: 'text.primary' }}>
                Personal Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={profileData?.firstName || ''}
                    onChange={handleChange}
                    disabled={!isEditing}
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-root.Mui-disabled': {
                        bgcolor: 'grey.100'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={profileData?.lastName || ''}
                    onChange={handleChange}
                    disabled={!isEditing}
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-root.Mui-disabled': {
                        bgcolor: 'grey.100'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Father's Name"
                    name="fatherName"
                    value={profileData?.fatherName || ''}
                    onChange={handleChange}
                    disabled={!isEditing}
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-root.Mui-disabled': {
                        bgcolor: 'grey.100'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Mobile"
                    name="mobile"
                    value={profileData?.mobile || ''}
                    onChange={handleChange}
                    disabled={!isEditing}
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-root.Mui-disabled': {
                        bgcolor: 'grey.100'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={profileData?.email || ''}
                    onChange={handleChange}
                    disabled={!isEditing}
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-root.Mui-disabled': {
                        bgcolor: 'grey.100'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Date of Birth"
                    name="dob"
                    type="date"
                    value={profileData?.dob || ''}
                    onChange={handleChange}
                    disabled={!isEditing}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-root.Mui-disabled': {
                        bgcolor: 'grey.100'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined" disabled={!isEditing}>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      name="gender"
                      value={profileData?.gender || ''}
                      onChange={handleChange}
                      label="Gender"
                      sx={{
                        '&.Mui-disabled': {
                          bgcolor: 'grey.100'
                        }
                      }}
                    >
                      <MenuItem value="" disabled>
                        Select Gender
                      </MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Address Information Section */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: 'text.primary' }}>
                Address Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Address Line 1"
                    name="studentAddressInfo.addressLine1"
                    value={profileData?.studentAddressInfo?.addressLine1 || ''}
                    onChange={handleChange}
                    disabled={!isEditing}
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-root.Mui-disabled': {
                        bgcolor: 'grey.100'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Address Line 2"
                    name="studentAddressInfo.addressLine2"
                    value={profileData?.studentAddressInfo?.addressLine2 || ''}
                    onChange={handleChange}
                    disabled={!isEditing}
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-root.Mui-disabled': {
                        bgcolor: 'grey.100'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    name="studentAddressInfo.city"
                    value={profileData?.studentAddressInfo?.city || ''}
                    onChange={handleChange}
                    disabled={!isEditing}
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-root.Mui-disabled': {
                        bgcolor: 'grey.100'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="State"
                    name="studentAddressInfo.state"
                    value={profileData?.studentAddressInfo?.state || ''}
                    onChange={handleChange}
                    disabled={!isEditing}
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-root.Mui-disabled': {
                        bgcolor: 'grey.100'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Country"
                    name="studentAddressInfo.country"
                    value={profileData?.studentAddressInfo?.country || ''}
                    onChange={handleChange}
                    disabled={!isEditing}
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-root.Mui-disabled': {
                        bgcolor: 'grey.100'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Postal Code"
                    name="studentAddressInfo.postalCode"
                    value={profileData?.studentAddressInfo?.postalCode || ''}
                    onChange={handleChange}
                    disabled={!isEditing}
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-root.Mui-disabled': {
                        bgcolor: 'grey.100'
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Profile;